import { useState } from 'react';
import Modal from '@/components/ui/Modal';
import { GRAVEDAD_LABEL, type Gravedad } from '@/lib/dispositivos';
import { useCatalogosDispositivos } from './catalogos';

/**
 * Una entrada del registro de incidencias. No es un ticket: fecha, tipo, qué
 * pasó y, si se sabe ya, qué se hizo. Lo que importa es que al abrir la ficha
 * o al ir a instalarlo se vea que este aparato ya ha dado guerra.
 */
interface Props {
  abierto: boolean;
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
}

export default function ModalIncidencia({ abierto, onCerrar, onGuardar }: Props) {
  const { catalogos } = useCatalogosDispositivos();
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const [fecha, setFecha] = useState(hoy);
  const [incidenciaTipo, setTipo] = useState('');
  const [gravedad, setGravedad] = useState<Gravedad>('leve');
  const [descripcion, setDescripcion] = useState('');
  const [queSeHizo, setQueSeHizo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tipos = catalogos.incidenciaTipos;
  const tipoElegido = incidenciaTipo || tipos[0]?.id || '';

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar({ fecha, incidenciaTipo: tipoElegido, gravedad, descripcion: descripcion.trim(), queSeHizo: queSeHizo.trim() || undefined });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title="Apuntar incidencia">
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Cuándo</span>
            <input type="date" className="input mt-1" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Gravedad</span>
            <select className="input mt-1" value={gravedad} onChange={(e) => setGravedad(e.target.value as Gravedad)}>
              {(Object.keys(GRAVEDAD_LABEL) as Gravedad[]).map((g) => (
                <option key={g} value={g}>{GRAVEDAD_LABEL[g]}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="label">Tipo *</span>
          <select className="input mt-1" value={tipoElegido} onChange={(e) => setTipo(e.target.value)}>
            {tipos.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">Qué pasó *</span>
          <textarea className="input mt-1" rows={3} required value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Qué se hizo</span>
          <textarea className="input mt-1" rows={2} value={queSeHizo} onChange={(e) => setQueSeHizo(e.target.value)} placeholder="Reinicio, pilas, reinstalado… (opcional)" />
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">{guardando ? 'Guardando…' : 'Apuntar'}</button>
        </div>
      </form>
    </Modal>
  );
}
